/**
 * splash_crowns — the ring a splash leaves on the water (R17 of the
 * 2026-09-25 water-realism wave).
 *
 * A body entering the water threw only a particle burst; the surface itself
 * showed nothing but the interaction field's slope. A crown is a bright,
 * thinning foam ring that spreads from the impact and fades (the "crown"
 * of a drop impact, then its capillary ring): a flat textured quad on the
 * water, scaled out over its life. Pooled; each quad OWNS its material (a
 * timed quad animates `opacity` — the tracer lesson: a shared material
 * blinks every sibling). Scene children in ABSOLUTE coordinates. An overlay
 * (`markMrtOverlay`): the water under it keeps its normal.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { disposeLater } from './gpu_dispose.js';

export const CROWN_POOL = 12;
export const CROWN_LIFE_S = 1.1;
/** Radius (m) a crown of strength 1 reaches. */
export const CROWN_REACH_M = 2.2;

/** The crown's radius and opacity at age `t` (s) for a strength (the harness's oracle). */
export function crownAt(t: number, strength: number): { radius: number; opacity: number } {
  const u = Math.min(1, Math.max(0, t / CROWN_LIFE_S));
  const ease = 1 - (1 - u) * (1 - u);
  return { radius: (0.15 + CROWN_REACH_M * strength * ease), opacity: (1 - u) * (1 - u) * 0.85 };
}

let ringTex: THREE.Texture | null = null;
function ringTexture(): THREE.Texture {
  if (ringTex) return ringTex;
  const N = 128;
  const data = new Uint8Array(N * N * 4);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const dx = (x + 0.5) / N * 2 - 1, dy = (y + 0.5) / N * 2 - 1;
    const r = Math.hypot(dx, dy);
    // A bright rim near r 0.85 with a ragged, noisy inner lace.
    const rim = Math.exp(-((r - 0.84) ** 2) / 0.004);
    const lace = r < 0.84 ? 0.25 * Math.max(0, Math.sin(Math.atan2(dy, dx) * 23 + r * 40) * Math.sin(r * 57)) * r : 0;
    const a = Math.min(1, rim + lace) * (r < 1 ? 1 : 0);
    const i = (y * N + x) * 4;
    data[i] = 240; data[i + 1] = 247; data[i + 2] = 250; data[i + 3] = Math.round(a * 255);
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter; t.generateMipmaps = true;
  t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true;
  ringTex = t;
  return t;
}

interface Crown { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; age: number; strength: number; live: boolean }

export class SplashCrowns {
  private readonly pool: Crown[] = [];
  private readonly scene: THREE.Scene;
  private next = 0;
  private spawned = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    const geo = new THREE.PlaneGeometry(2, 2);
    geo.rotateX(-Math.PI / 2);
    for (let i = 0; i < CROWN_POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({ map: ringTexture(), transparent: true, depthWrite: false, opacity: 0, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
      markMrtOverlay(mat);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = 'SplashCrown';
      mesh.visible = false;
      mesh.renderOrder = 4;
      mesh.frustumCulled = false;
      scene.add(mesh);
      this.pool.push({ mesh, mat, age: 0, strength: 1, live: false });
    }
  }

  /** A crown at (x, y, z) — ABSOLUTE; `strength` ~0.3 (a stride) … 1.6 (a body falling in). */
  spawn(x: number, y: number, z: number, strength: number): void {
    const c = this.pool[this.next];
    this.next = (this.next + 1) % this.pool.length;
    c.mesh.position.set(x, y + 0.03, z);
    c.age = 0; c.strength = Math.max(0.2, Math.min(2, strength)); c.live = true;
    c.mesh.visible = true;
    this.spawned++;
  }

  update(dt: number, _scenePos: THREE.Vector3): void {
    const d = Math.min(0.1, Math.max(0, dt));
    for (const c of this.pool) {
      if (!c.live) continue;
      c.age += d;
      if (c.age >= CROWN_LIFE_S) { c.live = false; c.mesh.visible = false; c.mat.opacity = 0; continue; }
      const k = crownAt(c.age, c.strength);
      c.mesh.scale.setScalar(k.radius);
      c.mat.opacity = k.opacity;
    }
  }

  stats(): object { return { spawned: this.spawned, live: this.pool.filter((c) => c.live).length }; }

  dispose(): void {
    for (const c of this.pool) { this.scene.remove(c.mesh); disposeLater(c.mat); }
    if (this.pool[0]) disposeLater(this.pool[0].mesh.geometry);
    this.pool.length = 0;
  }
}
